import { useState, useRef, useCallback } from 'react';

interface UseHorizontalSwipeProps {
  threshold?: number;
  initialState?: boolean;
}

/**
 * A minimal hook for horizontal swipe detection
 * 
 * @param threshold Minimum distance in pixels to trigger a swipe (default: 40)
 * @param initialState Initial state of the swipe (default: false)
 * @returns Object containing swipe state and handlers
 */
export const useHorizontalSwipe = ({
  threshold = 40,
  initialState = false,
}: UseHorizontalSwipeProps = {}) => {
  // Single state to track if swiped
  const [isSwiped, setIsSwiped] = useState(initialState);
  
  // Refs to track swipe coordinates
  const startXRef = useRef(0);
  const currentXRef = useRef(0);
  const isSwiping = useRef(false);

  // Reset swipe state
  const resetSwipe = useCallback(() => {
    setIsSwiped(false);
  }, []);

  // Set swipe state manually
  const setSwipeState = useCallback((state: boolean) => {
    setIsSwiped(state);
  }, []);

  // Handle swipe detection
  const detectSwipe = useCallback((endX: number) => {
    if (!isSwiping.current) return;
    
    const diff = startXRef.current - endX;
    
    // Swipe left to open
    if (diff > threshold && !isSwiped) {
      setIsSwiped(true);
    } 
    // Swipe right to close
    else if (diff < -threshold && isSwiped) {
      setIsSwiped(false);
    }
    
    isSwiping.current = false;
  }, [threshold, isSwiped]);

  // Touch event handlers
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    startXRef.current = e.touches[0].clientX;
    currentXRef.current = e.touches[0].clientX;
    isSwiping.current = true;
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isSwiping.current) return;
    currentXRef.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    const endX = e.changedTouches.length > 0 
      ? e.changedTouches[0].clientX 
      : currentXRef.current;
    detectSwipe(endX);
  }, [detectSwipe]);

  // Mouse event handlers
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault(); // Prevent text selection
    startXRef.current = e.clientX;
    currentXRef.current = e.clientX;
    isSwiping.current = true;
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isSwiping.current) return;
    currentXRef.current = e.clientX;
    e.preventDefault();
  }, []);

  const handleMouseUp = useCallback((e: React.MouseEvent) => {
    detectSwipe(currentXRef.current || e.clientX);
  }, [detectSwipe]);

  // Combine all handlers
  const swipeHandlers = {
    onTouchStart: handleTouchStart,
    onTouchMove: handleTouchMove,
    onTouchEnd: handleTouchEnd,
    onMouseDown: handleMouseDown,
    onMouseMove: handleMouseMove,
    onMouseUp: handleMouseUp,
    onMouseLeave: handleMouseUp, // Handle case when mouse leaves the element
  };

  return {
    isSwiped,
    resetSwipe,
    setIsSwiped: setSwipeState,
    swipeHandlers,
  };
};
